import { logger } from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, Menu, LogOut, Home, ClipboardList, BarChart3, Newspaper,
  Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink, X, Sparkles, Bot
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { titulo: '', resumo: '', conteudo: '', categoria: 'Tutoriais', capa: '', publicado: true };
const CATEGORIAS = ['Tutoriais', 'Dicas', 'Firmware', 'Comparativos', 'Guias', 'Notícias'];

const parseArtigoIA = (text) => {
  const out = {};
  const titulo = text.match(/^TITULO:\s*(.+)$/m);
  const resumo = text.match(/^RESUMO:\s*(.+)$/m);
  const cat = text.match(/^CATEGORIA:\s*(.+)$/m);
  const idx = text.indexOf('CONTEUDO:');
  if (titulo) out.titulo = titulo[1].trim();
  if (resumo) out.resumo = resumo[1].trim().slice(0, 300);
  if (cat && CATEGORIAS.includes(cat[1].trim())) out.categoria = cat[1].trim();
  if (idx !== -1) out.conteudo = text.slice(idx + 'CONTEUDO:'.length).trim();
  return out;
};

const AdminBlog = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [aiTema, setAiTema] = useState('');
  const [aiGerando, setAiGerando] = useState(false);

  const handleGerarIA = async () => {
    if (!aiTema.trim()) {
      toast({ title: 'Digite o tema do artigo', variant: 'destructive' });
      return;
    }
    setAiGerando(true);
    setForm((f) => ({ ...f, conteudo: '' }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/blog/admin/gerar`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tema: aiTema.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        setForm((f) => ({ ...f, conteudo: full }));
      }
      if (full.includes('[ERRO:')) throw new Error('Falha na geração');
      const parsed = parseArtigoIA(full);
      setForm((f) => ({ ...f, ...parsed }));
      toast({ title: 'Rascunho gerado! Revise antes de publicar.' });
    } catch (error) {
      logger.error('Erro ao gerar artigo com IA:', error);
      toast({ title: 'Erro ao gerar artigo com IA', variant: 'destructive' });
    } finally {
      setAiGerando(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog/admin/posts`, { withCredentials: true });
      setPosts(res.data);
    } catch (error) {
      logger.error('Erro ao buscar posts:', error);
      toast({ title: 'Erro ao carregar posts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await axios.get(`${BACKEND_URL}/api/admin/verify`, { withCredentials: true });
        fetchPosts();
      } catch {
        navigate('/admin/login');
      }
    };
    verifyAuth();
    // eslint-disable-next-line
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    navigate('/admin/login');
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (post) => {
    setEditingId(post.id);
    setForm({
      titulo: post.titulo,
      resumo: post.resumo,
      conteudo: post.conteudo,
      categoria: post.categoria,
      capa: post.capa || '',
      publicado: post.publicado,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.titulo.trim() || !form.resumo.trim() || !form.conteudo.trim()) {
      toast({ title: 'Preencha título, resumo e conteúdo', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const payload = { ...form, capa: form.capa.trim() || null };
    try {
      if (editingId) {
        await axios.patch(`${BACKEND_URL}/api/blog/posts/${editingId}`, payload, { withCredentials: true });
        toast({ title: 'Artigo atualizado!' });
      } else {
        await axios.post(`${BACKEND_URL}/api/blog/posts`, payload, { withCredentials: true });
        toast({ title: 'Artigo criado!' });
      }
      setModalOpen(false);
      fetchPosts();
    } catch (error) {
      logger.error('Erro ao salvar post:', error);
      toast({ title: 'Erro ao salvar artigo', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const togglePublicado = async (post) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/blog/posts/${post.id}`, { publicado: !post.publicado }, { withCredentials: true });
      fetchPosts();
    } catch {
      toast({ title: 'Erro ao alterar status', variant: 'destructive' });
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Excluir o artigo "${post.titulo}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/blog/posts/${post.id}`, { withCredentials: true });
      toast({ title: 'Artigo excluído' });
      fetchPosts();
    } catch {
      toast({ title: 'Erro ao excluir', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 border-r border-gray-800 transition-all duration-300`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-8">
            {sidebarOpen && <h1 className="text-xl font-bold text-yellow-400">WING ADMIN</h1>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white p-2">
              <Menu className="w-6 h-6" />
            </button>
          </div>
          <AdminNav active="blog" navigate={navigate} sidebarOpen={sidebarOpen} onLogout={handleLogout} />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Blog</h1>
              <p className="text-gray-400">Gerencie os artigos do site ({posts.length})</p>
            </div>
            <button
              onClick={openCreate}
              data-testid="new-post-btn"
              className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-5 py-2.5 rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" /> Novo Artigo
            </button>
          </div>

          <div className="space-y-4" data-testid="admin-posts-list">
            {posts.length === 0 ? (
              <p className="text-gray-400 text-center py-12">Nenhum artigo ainda. Clique em "Novo Artigo" para começar.</p>
            ) : (
              posts.map((post) => (
                <div key={post.id} className="glass-dark border border-white/10 rounded-xl p-5 flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${post.publicado ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
                        {post.publicado ? 'PUBLICADO' : 'RASCUNHO'}
                      </span>
                      <span className="text-xs text-gray-500">{post.categoria} • {post.tempo_leitura} min • {new Date(post.data_criacao).toLocaleDateString('pt-BR')}</span>
                    </div>
                    <h3 className="text-white font-bold">{post.titulo}</h3>
                    <p className="text-gray-500 text-sm truncate">{post.resumo}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" title="Ver no site" className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button onClick={() => togglePublicado(post)} title={post.publicado ? 'Despublicar' : 'Publicar'} className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-gray-800 rounded-lg" data-testid={`toggle-publish-${post.slug}`}>
                      {post.publicado ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => openEdit(post)} title="Editar" className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-800 rounded-lg" data-testid={`edit-post-${post.slug}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(post)} title="Excluir" className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded-lg" data-testid={`delete-post-${post.slug}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal criar/editar */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="post-modal">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
              <h2 className="text-xl font-bold text-white">{editingId ? 'Editar Artigo' : 'Novo Artigo'}</h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-white p-1" data-testid="close-modal-btn">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-bold text-yellow-400">Gerar rascunho com IA (GPT-5.5)</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">A IA escreve o artigo usando o manual oficial da WING como base. Revise sempre antes de publicar.</p>
                <div className="flex gap-2">
                  <input
                    value={aiTema}
                    onChange={(e) => setAiTema(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !aiGerando && handleGerarIA()}
                    disabled={aiGerando}
                    data-testid="ai-tema-input"
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-yellow-500 outline-none text-sm disabled:opacity-50"
                    placeholder="Ex: Como usar Sends on Faders na WING"
                  />
                  <button
                    onClick={handleGerarIA}
                    disabled={aiGerando}
                    data-testid="ai-gerar-btn"
                    className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50 text-sm whitespace-nowrap"
                  >
                    <Sparkles className="w-4 h-4" />
                    {aiGerando ? 'Gerando...' : 'Gerar'}
                  </button>
                </div>
                {aiGerando && (
                  <p className="text-xs text-yellow-400/80 mt-2 animate-pulse" data-testid="ai-gerando-status">
                    Escrevendo o artigo em tempo real no campo Conteúdo abaixo... (30–90 segundos)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Título *</label>
                <input
                  value={form.titulo}
                  onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                  data-testid="post-titulo-input"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-yellow-500 outline-none"
                  placeholder="Ex: Como configurar a Behringer WING do zero"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Resumo (aparece no Google e na lista) *</label>
                <textarea
                  value={form.resumo}
                  onChange={(e) => setForm({ ...form, resumo: e.target.value })}
                  data-testid="post-resumo-input"
                  rows={2}
                  maxLength={300}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-yellow-500 outline-none resize-none"
                  placeholder="Descrição curta do artigo (até 300 caracteres)"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Categoria</label>
                  <select
                    value={form.categoria}
                    onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                    data-testid="post-categoria-select"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-yellow-500 outline-none"
                  >
                    {CATEGORIAS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">URL da imagem de capa (opcional)</label>
                  <input
                    value={form.capa}
                    onChange={(e) => setForm({ ...form, capa: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-yellow-500 outline-none"
                    placeholder="https://..."
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Conteúdo (Markdown) *</label>
                <textarea
                  value={form.conteudo}
                  onChange={(e) => setForm({ ...form, conteudo: e.target.value })}
                  data-testid="post-conteudo-input"
                  rows={14}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-yellow-500 outline-none font-mono text-sm"
                  placeholder={'## Título da seção\n\nParágrafo normal com **negrito**.\n\n- Item de lista\n- Outro item\n\n> Dica em destaque'}
                />
                <p className="text-xs text-gray-500 mt-1">Use ## para títulos, **texto** para negrito, - para listas, &gt; para citações e [texto](/link) para links.</p>
              </div>
              <label className="flex items-center gap-2 text-gray-300 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.publicado}
                  onChange={(e) => setForm({ ...form, publicado: e.target.checked })}
                  data-testid="post-publicado-checkbox"
                  className="w-4 h-4 accent-yellow-500"
                />
                Publicar imediatamente
              </label>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-800 sticky bottom-0 bg-gray-900">
              <button onClick={() => setModalOpen(false)} className="px-5 py-2.5 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors">
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-post-btn"
                className="px-6 py-2.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black font-bold transition-colors disabled:opacity-50"
              >
                {saving ? 'Salvando...' : editingId ? 'Salvar Alterações' : 'Criar Artigo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
